import { handleApiErrors } from './toast-error.util';

export const BASE_URL = process.env.REACT_APP_API_URL;

class ApiService {
  token;
  user;

  setAuthToken(token) {
    this.token = token;
  }

  setUser(user) {
    this.user = user;
  }

  async updateToken() {
    this.token = await this.user?.getIdToken(true);
  }

  async requestWithTokenUpdate(params) {
    const rawResponse = await this.request(params);
    const response = await rawResponse.json();

    params.retries = (params?.retries || 0) + 1;

    const rawResponseStatus = rawResponse.status;

    const { status: statusCode, result, error, message } = response;
    if (statusCode >= 400 || rawResponseStatus >= 400) {
      if (statusCode === 401 && params.retries === 1) {
        await this.updateToken();

        return await this.requestWithTokenUpdate(params);
      }
      const errors = [];

      errors.push(result?.error || error || message);

      handleApiErrors((errors.length && errors) || statusCode);
      return Promise.reject(result);
    }

    return Promise.resolve(result);
  }

  async request({ endpoint, method, body, query, headers = {} }) {
    const url = query
      ? `${BASE_URL}/${endpoint}${query}`
      : `${BASE_URL}/${endpoint}`;
    const _body = body && JSON.stringify(body);
    const auth = this.token ? { Authorization: `Bearer ${this.token}` } : {};
    const _headers = { ...auth, ...headers };

    return await fetch(url, {
      method,
      headers: _headers,
      body: _body,
    });
  }

  async get(endpoint, query = '') {
    return this.requestWithTokenUpdate({ endpoint, method: 'GET', query });
  }

  async post(endpoint, body) {
    const headers = {
      'Content-Type': 'application/json',
    };

    return this.requestWithTokenUpdate({
      endpoint,
      method: 'POST',
      body,
      headers,
    });
  }

  async put(endpoint, body) {
    const headers = {
      'Content-Type': 'application/json',
    };

    return this.requestWithTokenUpdate({
      endpoint,
      method: 'PUT',
      body,
      headers,
    });
  }

  async patch(endpoint, body) {
    const headers = {
      'Content-Type': 'application/json',
    };

    return this.requestWithTokenUpdate({
      endpoint,
      method: 'PATCH',
      body,
      headers,
    });
  }

  async getJson(endpoint, query = '') {
    const rawResponse = await this.request({ endpoint, method: 'GET', query });
    const response = await rawResponse.json();
    const rawStatus = rawResponse.status;
    if (rawStatus >= 400) {
      return Promise.reject(response);
    }
    return Promise.resolve(response);
  }

  async postJson(endpoint, body) {
    const rawResponse = await this.request({
      endpoint,
      method: 'POST',
      body,
      headers: { 'Content-Type': 'application/json' },
    });
    const response = await rawResponse.json();
    const rawStatus = rawResponse.status;
    if (rawStatus >= 400) {
      return Promise.reject(response);
    }
    return Promise.resolve(response);
  }

  async getText(endpoint) {
    const rawResponse = await this.request({ endpoint, method: 'GET' });
    if (rawResponse.status >= 400) {
      return Promise.reject(await rawResponse.json());
    }
    return rawResponse.text();
  }

  async delete(endpoint) {
    return this.requestWithTokenUpdate({ endpoint, method: 'DELETE' });
  }
}

const api = new ApiService();

export default api;
